import { useCallback, useEffect, useRef, useState } from "react";
import { doc, serverTimestamp, setDoc, updateDoc } from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import { useAuth } from "@/contexts/AuthContext";
import { db } from "@/integrations/firebase/firestore";
import { logError } from "@/shared/services/ErrorReporting";
import type {
  BookDetailsArea,
  FeedbackCategory,
  FeedbackContentType,
  FeedbackRecord,
} from "@/types/feedback";

export type FeedbackRating = "positive" | "negative";

type NewFeedback = Omit<FeedbackRecord, "updated_at">;

type FeedbackUpdate = Partial<
  Omit<
    FeedbackRecord,
    | "id"
    | "updated_at"
    | "user_id"
    | "job_id"
    | "feature_type"
    | "section_index"
  >
>;

const COLLECTION = "feedbacks";

async function createFeedback(record: NewFeedback): Promise<boolean> {
  try {
    await setDoc(doc(db, COLLECTION, record.id), {
      ...record,
      updated_at: serverTimestamp(),
    });
    return true;
  } catch (err) {
    logError(err as Error);
    return false;
  }
}

async function updateFeedback(
  id: string,
  partial: FeedbackUpdate,
): Promise<boolean> {
  try {
    await updateDoc(doc(db, COLLECTION, id), {
      ...partial,
      updated_at: serverTimestamp(),
    });
    return true;
  } catch (err) {
    logError(err as Error);
    return false;
  }
}

interface UseFeedbackOptions {
  jobId: string;
  contentType: FeedbackContentType;
  sectionIndex?: number | null;
}

const TEXT_DEBOUNCE_MS = 1500;

interface UseFeedbackReturn {
  rating: FeedbackRating | null;
  categories: FeedbackCategory[];
  area: BookDetailsArea | null;
  comment: string;
  isExpanded: boolean;
  isSubmitting: boolean;
  handleRate: (rating: FeedbackRating) => void;
  handleToggleCategory: (category: FeedbackCategory) => void;
  handleSelectArea: (area: BookDetailsArea) => void;
  handleCommentChange: (value: string) => void;
  handleSubmit: () => Promise<boolean>;
  handleCancel: () => void;
}

export function useFeedback({
  jobId,
  contentType,
  sectionIndex = null,
}: UseFeedbackOptions): UseFeedbackReturn {
  const { authUser } = useAuth();
  const userId = authUser?.uid;

  const [rating, setRating] = useState<FeedbackRating | null>(null);
  const [categories, setCategories] = useState<FeedbackCategory[]>([]);
  const [area, setArea] = useState<BookDetailsArea | null>(null);
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const docIdRef = useRef<string | null>(null);
  const createPromiseRef = useRef<Promise<boolean> | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingCommentRef = useRef<string | null>(null);

  const isExpanded = rating !== null;

  const clearDebounce = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  const writeUpdate = useCallback(
    async (
      partial: FeedbackUpdate,
      docId = docIdRef.current,
    ): Promise<boolean> => {
      if (!docId || !userId) return false;
      const pendingCreate = createPromiseRef.current;
      if (pendingCreate) {
        const created = await pendingCreate;
        if (!created) return false;
      }
      return await updateFeedback(docId, partial);
    },
    [userId],
  );

  const flushPendingWrite = useCallback(async (): Promise<boolean> => {
    const text = pendingCommentRef.current;
    pendingCommentRef.current = null;
    clearDebounce();
    if (text === null) return true;
    return await writeUpdate({ text_feedback: text });
  }, [clearDebounce, writeUpdate]);

  const resetState = useCallback(() => {
    setRating(null);
    setCategories([]);
    setArea(null);
    setComment("");
    docIdRef.current = null;
    createPromiseRef.current = null;
    pendingCommentRef.current = null;
    clearDebounce();
  }, [clearDebounce]);

  const startNewRecord = useCallback(
    async (newRating: FeedbackRating) => {
      if (!userId) return;
      const id = uuidv4();
      docIdRef.current = id;
      const createPromise = createFeedback({
        id,
        job_id: jobId,
        user_id: userId,
        feature_type: contentType,
        section_index: sectionIndex,
        is_positive: newRating === "positive",
        status: "Draft",
        text_feedback: "",
        categorical_feedback: newRating === "positive" ? null : [],
        area: null,
      }).then((ok) => {
        if (!ok && docIdRef.current === id) docIdRef.current = null;
        return ok;
      });
      createPromiseRef.current = createPromise;
      await createPromise;
    },
    [userId, jobId, contentType, sectionIndex],
  );

  const handleCancel = useCallback(() => {
    const docId = docIdRef.current;
    const wasPositive = rating === "positive";
    void (async () => {
      await flushPendingWrite();
      if (docId) {
        await writeUpdate(
          {
            status: "Cancelled",
            text_feedback: "",
            categorical_feedback: wasPositive ? null : [],
            area: null,
          },
          docId,
        );
      }
    })();
    resetState();
  }, [rating, flushPendingWrite, writeUpdate, resetState]);

  const handleRate = useCallback(
    (newRating: FeedbackRating) => {
      if (rating === newRating) {
        handleCancel();
        return;
      }

      if (rating !== null && rating !== newRating) {
        void flushPendingWrite();
      }

      setCategories([]);
      setArea(null);
      setComment("");
      pendingCommentRef.current = null;
      clearDebounce();
      setRating(newRating);
      void startNewRecord(newRating);
    },
    [rating, flushPendingWrite, clearDebounce, startNewRecord, handleCancel],
  );

  const handleToggleCategory = useCallback(
    (category: FeedbackCategory) => {
      if (rating === "positive") return;
      setCategories((prev) => {
        const next = prev.includes(category)
          ? prev.filter((c) => c !== category)
          : [...prev, category];
        void writeUpdate({ categorical_feedback: next });
        return next;
      });
    },
    [rating, writeUpdate],
  );

  const handleSelectArea = useCallback(
    (nextArea: BookDetailsArea) => {
      setArea((prev) => {
        const resolved = prev === nextArea ? null : nextArea;
        void writeUpdate({ area: resolved });
        return resolved;
      });
    },
    [writeUpdate],
  );

  const handleCommentChange = useCallback(
    (value: string) => {
      setComment(value);
      if (rating === null) return;
      pendingCommentRef.current = value;
      clearDebounce();
      debounceTimerRef.current = setTimeout(() => {
        const text = pendingCommentRef.current;
        pendingCommentRef.current = null;
        debounceTimerRef.current = null;
        if (text !== null) void writeUpdate({ text_feedback: text });
      }, TEXT_DEBOUNCE_MS);
    },
    [rating, clearDebounce, writeUpdate],
  );

  const handleSubmit = useCallback(async (): Promise<boolean> => {
    if (!docIdRef.current || rating === null) return false;
    setIsSubmitting(true);
    const docId = docIdRef.current;
    try {
      const flushed = await flushPendingWrite();
      if (!flushed) return false;
      const submitted = await writeUpdate({ status: "Submitted" }, docId);
      if (submitted) resetState();
      return submitted;
    } finally {
      setIsSubmitting(false);
    }
  }, [rating, flushPendingWrite, writeUpdate, resetState]);

  useEffect(() => () => clearDebounce(), [clearDebounce]);

  return {
    rating,
    categories,
    area,
    comment,
    isExpanded,
    isSubmitting,
    handleRate,
    handleToggleCategory,
    handleSelectArea,
    handleCommentChange,
    handleSubmit,
    handleCancel,
  };
}
